import React, { useState, useRef, useEffect } from 'react'
import {X, Upload, Twitter, Globe, BarChart2, Facebook, Instagram, MessageCircleDashed as MessageCircle, Mail, Send, Trash2, Image, Video} from 'lucide-react'
import { Project } from '../hooks/useProjects'
import { useFileUpload } from '../hooks/useFileUpload'
import { useProjects } from '../hooks/useProjects'

interface ProfileEditModalProps {
  project: Project
  onClose: () => void
}

interface NewMediaFile {
  file: File
  type: 'image' | 'video'
  caption: string
}

export function ProfileEditModal({ project, onClose }: ProfileEditModalProps) {
  const { updateProject } = useProjects()
  const { uploadSingleFile, uploading } = useFileUpload()
  
  const [formData, setFormData] = useState({
    description: project.description || '',
    websiteUrl: project.websiteUrl || '',
    blockchain: project.blockchain || 'Base',
    tokenContractAddress: project.tokenContractAddress || '',
    nftContractAddress: project.nftContractAddress || '',
    subscriptionStatus: project.subscriptionStatus || 'pending',
    isActive: project.isActive ?? true,
    isDemo: project.isDemo ?? false,
    twitter: project.socialLinks?.twitter || '',
    baseApp: project.socialLinks?.baseApp || '',
    chart: project.socialLinks?.chart || '',
    farcaster: project.socialLinks?.farcaster || '',
    facebook: project.socialLinks?.facebook || '',
    instagram: project.socialLinks?.instagram || '',
    tiktok: project.socialLinks?.tiktok || '',
    email: project.socialLinks?.email || '',
    telegram: project.socialLinks?.telegram || '',
    nftMarketplace: project.socialLinks?.nftMarketplace || ''
  })
  
  const [bannerPreview, setBannerPreview] = useState(project.bannerImageUrl || '')
  const [profilePreview, setProfilePreview] = useState(project.profileImageUrl || '')
  const [bannerFile, setBannerFile] = useState<File | null>(null)
  const [profileFile, setProfileFile] = useState<File | null>(null)
  const [saving, setSaving] = useState(false)
  
  // Media gallery state
  const [mediaGallery, setMediaGallery] = useState(project.mediaGallery || [])
  const [newMediaFiles, setNewMediaFiles] = useState<NewMediaFile[]>([])
  
  const bannerInputRef = useRef<HTMLInputElement>(null)
  const profileInputRef = useRef<HTMLInputElement>(null)
  const mediaInputRef = useRef<HTMLInputElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)

  // Scroll to top when modal opens
  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.scrollTop = 0
    }
  }, [])

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, type: 'banner' | 'profile') => {
    const file = e.target.files?.[0]
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file')
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB')
        return
      }
      
      const preview = URL.createObjectURL(file)
      if (type === 'banner') {
        setBannerFile(file)
        setBannerPreview(preview)
      } else {
        setProfileFile(file)
        setProfilePreview(preview)
      }
    }
  }

  const handleMediaSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files) {
      Array.from(files).forEach(file => {
        const isImage = file.type.startsWith('image/')
        const isVideo = file.type.startsWith('video/')
        
        if (!isImage && !isVideo) {
          alert('Please select image or video files only')
          return
        }
        
        if (file.size > 50 * 1024 * 1024) {
          alert('File size must be less than 50MB')
          return
        }
        
        setNewMediaFiles(prev => [...prev, {
          file,
          type: isImage ? 'image' : 'video',
          caption: ''
        }])
      })
    }
  }

  const handleRemoveNewMedia = (index: number) => {
    setNewMediaFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleRemoveExistingMedia = (index: number) => {
    setMediaGallery(prev => prev.filter((_, i) => i !== index))
  }

  const handleCaptionChange = (index: number, caption: string) => {
    setNewMediaFiles(prev => prev.map((item, i) => 
      i === index ? { ...item, caption } : item
    ))
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      
      let bannerUrl = project.bannerImageUrl || ''
      let profileUrl = project.profileImageUrl || ''
      
      // Upload new banner if selected
      if (bannerFile) {
        console.log('Uploading banner file:', bannerFile.name, bannerFile.size, bannerFile.type)
        try {
          const uploadedUrl = await uploadSingleFile(bannerFile)
          console.log('Raw banner upload response:', uploadedUrl, typeof uploadedUrl)
          
          if (uploadedUrl && typeof uploadedUrl === 'string' && uploadedUrl.trim().length > 0) {
            // More lenient validation - accept http, https, or relative URLs
            const isValidUrl = uploadedUrl.startsWith('http://') || 
                              uploadedUrl.startsWith('https://') || 
                              uploadedUrl.startsWith('/')
            
            if (isValidUrl) {
              bannerUrl = uploadedUrl
              console.log('Banner uploaded successfully:', bannerUrl)
            } else {
              console.error('Invalid banner URL format:', uploadedUrl)
              alert(`Banner upload failed: Invalid URL format received (${uploadedUrl.substring(0, 50)})`)
              setSaving(false)
              return
            }
          } else {
            console.error('Invalid banner URL returned:', uploadedUrl)
            alert('Banner upload failed: No valid URL returned from upload service')
            setSaving(false)
            return
          }
        } catch (uploadError) {
          console.error('Banner upload error:', uploadError)
          alert(`Failed to upload banner image: ${uploadError instanceof Error ? uploadError.message : 'Unknown error'}`)
          setSaving(false)
          return
        }
      }
      
      // Upload new profile image if selected
      if (profileFile) {
        console.log('Uploading profile file:', profileFile.name, profileFile.size, profileFile.type)
        try {
          const uploadedUrl = await uploadSingleFile(profileFile)
          console.log('Raw profile upload response:', uploadedUrl, typeof uploadedUrl)
          
          if (uploadedUrl && typeof uploadedUrl === 'string' && uploadedUrl.trim().length > 0) {
            // More lenient validation - accept http, https, or relative URLs
            const isValidUrl = uploadedUrl.startsWith('http://') || 
                              uploadedUrl.startsWith('https://') || 
                              uploadedUrl.startsWith('/')
            
            if (isValidUrl) {
              profileUrl = uploadedUrl
              console.log('Profile uploaded successfully:', profileUrl)
            } else {
              console.error('Invalid profile URL format:', uploadedUrl)
              alert(`Profile upload failed: Invalid URL format received (${uploadedUrl.substring(0, 50)})`)
              setSaving(false)
              return
            }
          } else {
            console.error('Invalid profile URL returned:', uploadedUrl)
            alert('Profile upload failed: No valid URL returned from upload service')
            setSaving(false)
            return
          }
        } catch (uploadError) {
          console.error('Profile upload error:', uploadError)
          alert(`Failed to upload profile image: ${uploadError instanceof Error ? uploadError.message : 'Unknown error'}`)
          setSaving(false)
          return
        }
      }

      // Upload new media files
      const uploadedMedia = []
      for (const mediaItem of newMediaFiles) {
        try {
          const uploadedUrl = await uploadSingleFile(mediaItem.file)
          if (uploadedUrl && typeof uploadedUrl === 'string' && uploadedUrl.trim().length > 0) {
            uploadedMedia.push({
              url: uploadedUrl,
              type: mediaItem.type,
              caption: mediaItem.caption,
              uploadedAt: new Date().toISOString()
            })
          }
        } catch (error) {
          console.error('Media upload failed for:', mediaItem.file.name, error)
          // Continue with other files even if one fails
        }
      }

      // Combine existing and new media
      const updatedMediaGallery = [...mediaGallery, ...uploadedMedia]

      // Generate slug from project name if not exists
      const slug = project.slug || formData.websiteUrl || project.projectName.toLowerCase().replace(/[^a-z0-9]+/g, '-')
      
      // Final validation before saving
      if (bannerUrl && bannerUrl.length > 0) {
        const isValidBanner = bannerUrl.startsWith('http://') || 
                             bannerUrl.startsWith('https://') || 
                             bannerUrl.startsWith('/')
        if (!isValidBanner) {
          console.error('Invalid banner URL format:', bannerUrl)
          alert('Banner URL format is invalid. Please try uploading again.')
          setSaving(false)
          return
        }
      }
      
      if (profileUrl && profileUrl.length > 0) {
        const isValidProfile = profileUrl.startsWith('http://') || 
                              profileUrl.startsWith('https://') || 
                              profileUrl.startsWith('/')
        if (!isValidProfile) {
          console.error('Invalid profile URL format:', profileUrl)
          alert('Profile URL format is invalid. Please try uploading again.')
          setSaving(false)
          return
        }
      }

      console.log('Updating project with:', { bannerUrl, profileUrl, mediaCount: updatedMediaGallery.length })
      
      // Update project with all data
      await updateProject(project._id, {
        description: formData.description,
        websiteUrl: formData.websiteUrl,
        blockchain: formData.blockchain as 'Base' | 'Ethereum' | 'Solana',
        tokenContractAddress: formData.tokenContractAddress,
        nftContractAddress: formData.nftContractAddress,
        bannerImageUrl: bannerUrl || '',
        profileImageUrl: profileUrl || '',
        mediaGallery: updatedMediaGallery,
        slug,
        subscriptionStatus: formData.subscriptionStatus,
        isActive: formData.isActive,
        isDemo: formData.isDemo,
        socialLinks: {
          twitter: formData.twitter,
          website: formData.websiteUrl,
          baseApp: formData.baseApp,
          chart: formData.chart,
          farcaster: formData.farcaster,
          facebook: formData.facebook,
          instagram: formData.instagram,
          tiktok: formData.tiktok,
          email: formData.email,
          telegram: formData.telegram,
          nftMarketplace: formData.nftMarketplace
        }
      })
      
      alert('Profile updated successfully!')
      onClose()
    } catch (error) {
      console.error('Save error:', error)
      alert('Failed to save profile. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div 
        ref={modalRef}
        className="bg-[#0d1339] border border-white/10 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8"
      >
        <div className="flex justify-between items-center mb-6 sticky top-0 bg-[#0d1339] z-10 pb-4">
          <h3 className="text-2xl font-bold text-white">Edit Profile</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Banner Image */}
          <div>
            <label className="block text-gray-300 mb-2">Banner Image (1200x400px recommended)</label>
            <input
              ref={bannerInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleFileSelect(e, 'banner')}
              className="hidden"
            />
            
            {bannerPreview ? (
              <div className="relative">
                <img
                  src={bannerPreview}
                  alt="Banner preview"
                  className="w-full h-48 object-cover rounded-lg border border-white/10"
                />
                <button
                  onClick={() => bannerInputRef.current?.click()}
                  className="absolute top-2 right-2 p-2 bg-[#0052FF] text-white rounded-lg hover:bg-[#2563EB]"
                >
                  <Upload className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => bannerInputRef.current?.click()}
                className="w-full h-48 border-2 border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center gap-3 hover:border-[#0052FF] transition-colors"
              >
                <Upload className="w-10 h-10 text-gray-500" />
                <span className="text-gray-400">Click to upload banner</span>
              </button>
            )}
          </div>

          {/* Profile Image */}
          <div>
            <label className="block text-gray-300 mb-2">Profile Picture (Square, 400x400px recommended)</label>
            <input
              ref={profileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleFileSelect(e, 'profile')}
              className="hidden"
            />
            
            <div className="flex items-center gap-4">
              {profilePreview ? (
                <div className="relative">
                  <img
                    src={profilePreview}
                    alt="Profile preview"
                    className="w-32 h-32 object-cover rounded-full border border-white/10"
                  />
                  <button
                    onClick={() => profileInputRef.current?.click()}
                    className="absolute bottom-0 right-0 p-2 bg-[#0052FF] text-white rounded-full hover:bg-[#2563EB]"
                  >
                    <Upload className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => profileInputRef.current?.click()}
                  className="w-32 h-32 border-2 border-dashed border-white/20 rounded-full flex flex-col items-center justify-center gap-2 hover:border-[#0052FF] transition-colors"
                >
                  <Upload className="w-6 h-6 text-gray-500" />
                  <span className="text-xs text-gray-400">Upload</span>
                </button>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-gray-300 mb-2">Project Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Tell visitors about your project..."
              rows={4}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
            />
          </div>

          {/* Admin Controls */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6 space-y-4">
            <h4 className="text-lg font-semibold text-white mb-4">Admin Controls</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Subscription Status */}
              <div>
                <label className="block text-gray-300 mb-2">Subscription Status</label>
                <select
                  value={formData.subscriptionStatus}
                  onChange={(e) => setFormData({ ...formData, subscriptionStatus: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#0052FF]"
                >
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="expired">Expired</option>
                </select>
              </div>

              {/* Active Status */}
              <div>
                <label className="block text-gray-300 mb-2">Active Status</label>
                <div className="flex items-center h-[52px]">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                    className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${
                      formData.isActive ? 'bg-green-500' : 'bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                        formData.isActive ? 'translate-x-9' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="ml-3 text-sm text-gray-300">
                    {formData.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            <div className="text-sm text-gray-400">
              <p><strong className="text-white">Note:</strong> Project must have <strong>subscriptionStatus: "active"</strong> AND <strong>isActive: true</strong> to appear on the website.</p>
            </div>
          </div>

          {/* Blockchain Selection */}
          <div>
            <label className="block text-gray-300 mb-2">Blockchain</label>
            <select
              value={formData.blockchain}
              onChange={(e) => setFormData({ ...formData, blockchain: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#0052FF]"
            >
              <option value="Base">Base</option>
              <option value="Ethereum">Ethereum</option>
              <option value="Solana">Solana</option>
            </select>
          </div>

          {/* Contract Addresses */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 mb-2">Token Contract Address</label>
              <input
                type="text"
                value={formData.tokenContractAddress}
                onChange={(e) => setFormData({ ...formData, tokenContractAddress: e.target.value })}
                placeholder="0x..."
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2">NFT Contract Address</label>
              <input
                type="text"
                value={formData.nftContractAddress}
                onChange={(e) => setFormData({ ...formData, nftContractAddress: e.target.value })}
                placeholder="0x..."
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
              />
            </div>
          </div>

          {/* Social Links */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 mb-2 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Website
              </label>
              <input
                type="url"
                value={formData.websiteUrl}
                onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                placeholder="https://yourproject.com"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2 flex items-center gap-2">
                <Twitter className="w-4 h-4" />
                X (Twitter)
              </label>
              <input
                type="url"
                value={formData.twitter}
                onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                placeholder="https://x.com/yourproject"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2">Base App</label>
              <input
                type="url"
                value={formData.baseApp}
                onChange={(e) => setFormData({ ...formData, baseApp: e.target.value })}
                placeholder="https://base.org/..."
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2 flex items-center gap-2">
                <BarChart2 className="w-4 h-4" />
                Chart
              </label>
              <input
                type="url"
                value={formData.chart}
                onChange={(e) => setFormData({ ...formData, chart: e.target.value })}
                placeholder="https://dexscreener.com/..."
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2 flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                Farcaster
              </label>
              <input
                type="url"
                value={formData.farcaster}
                onChange={(e) => setFormData({ ...formData, farcaster: e.target.value })}
                placeholder="https://warpcast.com/..."
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2 flex items-center gap-2">
                <Facebook className="w-4 h-4" />
                Facebook
              </label>
              <input
                type="url"
                value={formData.facebook}
                onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                placeholder="https://facebook.com/..."
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2 flex items-center gap-2">
                <Instagram className="w-4 h-4" />
                Instagram
              </label>
              <input
                type="url"
                value={formData.instagram}
                onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                placeholder="https://instagram.com/..."
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2">TikTok</label>
              <input
                type="url"
                value={formData.tiktok}
                onChange={(e) => setFormData({ ...formData, tiktok: e.target.value })}
                placeholder="https://tiktok.com/@..."
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="contact@yourproject.com"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2 flex items-center gap-2">
                <Send className="w-4 h-4" />
                Telegram
              </label>
              <input
                type="url"
                value={formData.telegram}
                onChange={(e) => setFormData({ ...formData, telegram: e.target.value })}
                placeholder="https://t.me/..."
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2">NFT Marketplace</label>
              <input
                type="url"
                value={formData.nftMarketplace}
                onChange={(e) => setFormData({ ...formData, nftMarketplace: e.target.value })}
                placeholder="https://opensea.io/collection/..."
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
              />
            </div>
          </div>

          {/* Media Gallery */}
          <div>
            <label className="block text-gray-300 mb-3">Media Gallery</label>
            
            {/* Existing Media */}
            {mediaGallery.length > 0 && (
              <div className="mb-4">
                <p className="text-sm text-gray-400 mb-3">Current Media ({mediaGallery.length})</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {mediaGallery.map((media: any, index: number) => (
                    <div key={index} className="relative group">
                      {media.type === 'image' ? (
                        <img
                          src={media.url}
                          alt={media.caption || 'Gallery image'}
                          className="w-full h-32 object-cover rounded-lg border border-white/10"
                        />
                      ) : (
                        <video
                          src={media.url}
                          className="w-full h-32 object-cover rounded-lg border border-white/10"
                          controls
                        />
                      )}
                      <button
                        onClick={() => handleRemoveExistingMedia(index)}
                        className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {media.caption && (
                        <p className="text-xs text-gray-400 mt-1 truncate">{media.caption}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* New Media Upload */}
            <input
              ref={mediaInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleMediaSelect}
              className="hidden"
            />
            
            {newMediaFiles.length > 0 && (
              <div className="mb-4">
                <p className="text-sm text-gray-400 mb-3">New Media to Upload ({newMediaFiles.length})</p>
                <div className="space-y-3">
                  {newMediaFiles.map((item, index) => (
                    <div key={index} className="bg-white/5 border border-white/10 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          {item.type === 'image' ? (
                            <Image className="w-6 h-6 text-blue-400" />
                          ) : (
                            <Video className="w-6 h-6 text-purple-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-white text-sm font-medium mb-2">{item.file.name}</p>
                          <input
                            type="text"
                            value={item.caption}
                            onChange={(e) => handleCaptionChange(index, e.target.value)}
                            placeholder="Add a caption (optional)"
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
                          />
                        </div>
                        <button
                          onClick={() => handleRemoveNewMedia(index)}
                          className="flex-shrink-0 p-2 text-red-400 hover:text-red-300 transition-colors"
                          title="Remove"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={() => mediaInputRef.current?.click()}
              className="w-full px-6 py-4 border-2 border-dashed border-white/20 rounded-lg flex items-center justify-center gap-3 hover:border-[#0052FF] transition-colors text-gray-400 hover:text-white"
            >
              <Upload className="w-5 h-5" />
              <span>Upload Images or Videos</span>
            </button>
            <p className="text-xs text-gray-500 mt-2">Supported: Images (JPG, PNG, GIF) and Videos (MP4, MOV). Max 50MB per file.</p>
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving || uploading}
            className="w-full px-6 py-3 bg-gradient-to-r from-[#0052FF] to-[#00D9FF] text-white rounded-lg font-bold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving || uploading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Saving...
              </div>
            ) : (
              'Save Profile'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
